import React, { memo, useState } from 'react';
import { Blok } from '../../interfaces/Blok.interface';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { StoryblokLink } from '../../interfaces/StoryblokLink.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import Image from '../Image/Image';
import classNames from 'classnames';
import Grid from '../Utils/Grid/Grid';
import Col from '../Utils/Col/Col';
import Heading, { HeadingTypes } from '../Heading/Heading';
import StoryblokLinkProvider from '../Utils/StoryblokLinkProvider/StoryblokLinkProvider';
import CustomLink from '../CustomLink/CustomLink';
import SbImage from '../SbImage/SbImage';

export interface FeaturedPanelProps extends Blok {
  background_image: StoryblokAsset;
  heading: string;
  body: string;
  link: StoryblokLink;
}

interface FeaturedPanelBlokProps {
  blok: FeaturedPanelProps;
}

const FeaturedPanel: React.FC<FeaturedPanelBlokProps> = ({ blok }) => {
  const { background_image, heading, body, link } = blok;

  const [isHovered, setIsHovered] = useState<boolean>(false);

  return (
    <StoryblokLinkProvider
      link={link}
      render={(link) => (
        <CustomLink
          url={link}
          label={heading}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div
            {...storyblokEditable(blok)}
            className="relative overflow-hidden lg:aspect-square"
          >
            {background_image?.filename && (
              <div className="absolute top-0 right-0 bottom-0 left-0">
                <div
                  className={classNames(
                    'w-full h-full transition-all ease-in-out duration-700',
                    {
                      'scale-100': !isHovered,
                      'scale-110': isHovered,
                    }
                  )}
                >
                  <SbImage asset={background_image} options={{ objectFit: 'cover', width: 900, height: 900, sizes: { base: 1536 } }} />
                </div>
              </div>
            )}
            <div
              className={classNames(
                'relative w-full h-full flex items-center justify-center transition-all ease-in-out duration-700 py-20 sm:py-16 lg:py-0',
                {
                  'bg-[#0A212E]/80': !isHovered,
                  'bg-[#0A212E]/90': isHovered,
                }
              )}
            >
              <Grid>
                <Col
                  span={{ default: 10, md: 8, lg: 10 }}
                  start={{ default: 2, md: 3, lg: 2 }}
                >
                  <div className="space-y-6">
                    {heading && (
                      <Heading
                        text={heading}
                        headingType={HeadingTypes.h2}
                      ></Heading>
                    )}
                    {body && <p className="text-system/white">{body}</p>}
                    <div>
                      <svg
                        width="64"
                        height="64"
                        viewBox="0 0 64 64"
                        xmlns="http://www.w3.org/2000/svg"
                        className={classNames(
                          'transition-all ease-in-out duration-700',
                          {
                            'fill-system/white translate-x-0': !isHovered,
                            'fill-text/body-accent translate-x-5': isHovered,
                          }
                        )}
                      >
                        <g clipPath="url(#clip0_514_805)">
                          <path d="M32 10.6667L28.24 14.4267L43.12 29.3334H10.6667V34.6667H43.12L28.24 49.5734L32 53.3334L53.3334 32.0001L32 10.6667Z" />
                        </g>
                        <defs>
                          <clipPath id="clip0_514_805">
                            <rect width="64" height="64" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                  </div>
                </Col>
              </Grid>
            </div>
          </div>
        </CustomLink>
      )}
    />
  );
};

export default memo(withBlokWrapper(FeaturedPanel, Theme.DARK));
